
import { useState } from 'react';
import LogoUploader from '../../../components/base/LogoUploader';

const SeoAuditPage = () => {
  const [logo, setLogo] = useState('');
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/seo-audit-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          website: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoChange = (logoUrl: string) => {
    setLogo(logoUrl);
    setShowLogoUploader(false);
    // Сохраняем логотип в localStorage для сохранения между сессиями
    if (logoUrl) {
      localStorage.setItem('siteLogo', logoUrl);
    } else {
      localStorage.removeItem('siteLogo');
    }
  };

  // Загружаем логотип при инициализации
  useState(() => {
    const savedLogo = localStorage.getItem('siteLogo');
    if (savedLogo) {
      setLogo(savedLogo);
    }
  });

  const auditSteps = [
    {
      step: '01',
      title: 'Технический анализ',
      description: 'Проверяем скорость загрузки, мобильную адаптацию, структуру URL, robots.txt, sitemap.xml',
      icon: 'ri-settings-3-line'
    },
    {
      step: '02',
      title: 'Контентный аудит',
      description: 'Анализируем качество контента, дублированные страницы, мета-теги, заголовки',
      icon: 'ri-file-text-line'
    },
    {
      step: '03',
      title: 'Анализ ссылок',
      description: 'Изучаем внутреннюю перелинковку, внешние ссылки, анкорный лист',
      icon: 'ri-links-line'
    },
    {
      step: '04',
      title: 'Конкурентный анализ',
      description: 'Сравниваем с конкурентами, выявляем их сильные и слабые стороны',
      icon: 'ri-bar-chart-line'
    },
    {
      step: '05',
      title: 'Семантическое ядро',
      description: 'Собираем и анализируем ключевые запросы, группируем по релевантности',
      icon: 'ri-key-line'
    },
    {
      step: '06',
      title: 'Отчет и рекомендации',
      description: 'Предоставляем детальный отчет с приоритизированным планом работ',
      icon: 'ri-file-list-3-line'
    }
  ];

  const benefits = [
    {
      title: 'Выявление всех проблем',
      description: 'Находим скрытые технические ошибки, которые мешают продвижению',
      icon: 'ri-bug-line'
    },
    {
      title: 'Приоритизация задач',
      description: 'Составляем план работ по важности для максимального эффекта',
      icon: 'ri-list-check-line'
    },
    {
      title: 'Конкурентные преимущества',
      description: 'Показываем, как обойти конкурентов в поисковой выдаче',
      icon: 'ri-trophy-line'
    },
    {
      title: 'Экономия бюджета',
      description: 'Избегаем неэффективных трат на неправильную оптимизацию',
      icon: 'ri-money-dollar-circle-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-4">
                {logo ? (
                  <img 
                    src={logo} 
                    alt="Логотип" 
                    className="h-10 w-auto object-contain cursor-pointer"
                    onClick={() => setShowLogoUploader(true)}
                  />
                ) : (
                  <div 
                    onClick={() => setShowLogoUploader(true)}
                    className="text-2xl font-bold cursor-pointer hover:text-blue-600 transition-colors"
                    style={{ fontFamily: '"Pacifico", serif' }}
                  >
                    logo
                  </div>
                )}
                <button
                  onClick={() => setShowLogoUploader(true)}
                  className="text-sm text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
                  title="Изменить логотип"
                >
                  <i className="ri-edit-line"></i>
                </button>
              </div>
              <nav className="hidden md:flex space-x-6">
                <a href="/#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Услуги</a>
                <a href="/#tariffs" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Тарифы</a>
                <a href="/#cases" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Кейсы</a>
                <a href="/blog" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Блог</a>
              </nav>
            </div>
            <button 
              onClick={() => document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Заказать аудит
            </button>
          </div>
        </div>
      </header>

      {/* Logo Uploader Modal */}
      {showLogoUploader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Загрузить логотип</h3>
              <button
                onClick={() => setShowLogoUploader(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <LogoUploader 
              onLogoChange={handleLogoChange}
              currentLogo={logo}
            />
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowLogoUploader(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=Professional%20SEO%20audit%20workspace%20with%20multiple%20computer%20screens%20showing%20website%20analytics%2C%20technical%20analysis%20charts%2C%20SEO%20tools%20interface%2C%20modern%20office%20environment%20with%20detailed%20reports%20and%20graphs&width=1920&height=1080&seq=seo-audit-hero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            SEO Аудит<br />
            <span className="text-blue-400">Вашего Сайта</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            Комплексный анализ сайта с выявлением всех проблем и детальным планом оптимизации
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Заказать аудит
            </button>
            <button 
              onClick={() => document.getElementById('audit-process')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Как проходит аудит
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Зачем нужен SEO аудит</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              SEO аудит — это фундамент успешного продвижения сайта
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className={`${benefit.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audit Process Section */}
      <section id="audit-process" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Этапы SEO аудита</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Пошаговый процесс комплексного анализа вашего сайта
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {auditSteps.map((step, index) => (
              <div key={index} className="relative bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {step.step}
                </div>
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${step.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Что вы получите</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Детальный отчет с конкретными рекомендациями
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20SEO%20audit%20report%20document%20with%20detailed%20analysis%20charts%2C%20technical%20recommendations%2C%20priority%20task%20list%2C%20modern%20business%20report%20layout%20with%20graphs%20and%20data%20visualization&width=600&height=400&seq=audit-report&orientation=landscape"
                alt="SEO аудит отчет"
                className="w-full rounded-xl shadow-lg object-cover object-top"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Технический отчет</h3>
                  <p className="text-gray-600">Полный список технических ошибок с инструкциями по исправлению</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Семантическое ядро</h3>
                  <p className="text-gray-600">Готовый список ключевых запросов для продвижения</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">План оптимизации</h3>
                  <p className="text-gray-600">Пошаговый план работ с приоритизацией задач</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Анализ конкурентов</h3>
                  <p className="text-gray-600">Сравнение с топ-конкурентами и рекомендации по обходу</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Консультация эксперта</h3>
                  <p className="text-gray-600">Личная встреча с SEO-специалистом для разбора отчета</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Order Form Section */}
      <section id="order-form" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Заказать SEO аудит</h2>
              <p className="text-xl text-gray-600">
                Получите профессиональный анализ вашего сайта уже через 3-5 дней
              </p>
            </div>
            
            <form 
              id="seo-audit-order"
              data-readdy-form
              onSubmit={handleSubmit} 
              className="bg-gray-50 rounded-xl p-8"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Ваше имя *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Введите ваше имя"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Телефон *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>
                
                <div>
                  <label htmlFor="website" className="block text-sm font-semibold text-gray-700 mb-2">
                    Сайт для аудита *
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="https://yoursite.com"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Дополнительная информация
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="Расскажите о специфике вашего бизнеса, целевой аудитории, конкурентах..."
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Отправка...' : 'Заказать SEO аудит'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Спасибо! Ваша заявка принята. Мы свяжемся с вами в течение часа для уточнения деталей.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  Произошла ошибка при отправке. Пожалуйста, попробуйте еще раз.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {logo ? (
                <img 
                  src={logo} 
                  alt="Логотип" 
                  className="h-12 w-auto object-contain mb-4"
                />
              ) : (
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                  logo
                </h3>
              )}
              <p className="text-gray-400 mb-4">
                Профессиональное SEO продвижение для вашего бизнеса
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Услуги</h4>
              <ul className="space-y-2">
                <li><a href="/services/seo-audit" className="text-gray-400 hover:text-white transition-colors cursor-pointer">SEO Аудит</a></li>
                <li><a href="/services/technical-optimization" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Техническая оптимизация</a></li>
                <li><a href="/services/content-marketing" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Контент-маркетинг</a></li>
                <li><a href="/services/linkbuilding" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Линкбилдинг</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Компания</h4>
              <ul className="space-y-2">
                <li><a href="/#cases" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Кейсы</a></li>
                <li><a href="/#tariffs" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Тарифы</a></li>
                <li><a href="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Блог</a></li>
                <li><a href="/#consultation-form" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Контакты</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Контакты</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  +7 (950) 360-35-20
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  info@seo-vektor.ru
                </li>
                <li className="flex items-center">
                  <i className="ri-time-line mr-2"></i>
                  Пн-Пт: 9:00-18:00
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 SEO Продвижение. Все права защищены. | 
              <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer ml-1">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SeoAuditPage;
